import { useCallback } from 'react'
import {
  Drawer,
  VStack,
  HStack,
  Text,
  Box,
  Heading,
  IconButton,
  Card,
  Badge,
  Table,
} from '@chakra-ui/react'
import {
  X,
  ChevronUp,
  ChevronDown,
  CreditCard,
  Calendar,
  Clock,
  Truck,
  FileText,
  Landmark,
} from 'lucide-react'
import { useSupplierPayment } from '../hooks'
import { LoadingState } from '@/shared/components/LoadingState'
import { ErrorState } from '@/shared/components/ErrorState'
import type { SupplierPaymentStatus, SupplierPaymentMode } from '../types'
import {
  SUPPLIER_PAYMENT_STATUS_LABELS,
  SUPPLIER_PAYMENT_STATUS_COLORS,
  SUPPLIER_PAYMENT_MODE_LABELS,
} from '../types'

interface SupplierPaymentDetailDrawerProps {
  paymentId: string | null
  isOpen: boolean
  onClose: () => void
  onNavigatePrevious?: () => void
  onNavigateNext?: () => void
  hasPrevious?: boolean
  hasNext?: boolean
}

export function SupplierPaymentDetailDrawer({
  paymentId,
  isOpen,
  onClose,
  onNavigatePrevious,
  onNavigateNext,
  hasPrevious = false,
  hasNext = false,
}: SupplierPaymentDetailDrawerProps) {
  const { data: payment, isLoading, error } = useSupplierPayment(paymentId ?? undefined)

  const handleInteractOutside = useCallback(
    (e: { detail: { originalEvent: { target: EventTarget | null } } }) => {
      const target = e.detail.originalEvent.target as HTMLElement
      const tableRow = target.closest('[data-payment-row]') as HTMLElement
      if (tableRow) return
      onClose()
    },
    [onClose]
  )

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  }

  const formatCurrency = (value: number | null | undefined) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(value ?? 0)
  }

  const formatDateTime = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // Calculer le total des allocations
  const totalAllocated = payment?.supplier_payment_allocations?.reduce((sum, a) => sum + (a.amount_allocated || 0), 0) || 0

  return (
    <Drawer.Root
      open={isOpen}
      placement="end"
      size="md"
      modal={false}
      onInteractOutside={handleInteractOutside}
    >
      <Drawer.Positioner pointerEvents="none">
        <Drawer.Content maxW="700px" pointerEvents="auto">
          <Drawer.Header borderBottomWidth="1px" py={4} overflow="visible">
            <Box display="flex" alignItems="center" w="full" gap={3}>
              {/* Navigation */}
              <Box display="flex" gap={1} flexShrink={0}>
                <IconButton
                  variant="ghost"
                  size="sm"
                  onClick={onNavigatePrevious}
                  disabled={!hasPrevious}
                  aria-label="Précédent"
                >
                  <ChevronUp size={18} />
                </IconButton>
                <IconButton
                  variant="ghost"
                  size="sm"
                  onClick={onNavigateNext}
                  disabled={!hasNext}
                  aria-label="Suivant"
                >
                  <ChevronDown size={18} />
                </IconButton>
              </Box>

              {/* Title */}
              <Box flex={1} minW={0}>
                {payment && (
                  <>
                    <HStack gap={2} mb={1}>
                      {payment.reference && (
                        <Badge variant="outline" size="sm" fontFamily="mono">
                          {payment.reference}
                        </Badge>
                      )}
                      <Badge
                        colorPalette={
                          SUPPLIER_PAYMENT_STATUS_COLORS[payment.status as SupplierPaymentStatus]
                        }
                        size="sm"
                      >
                        {SUPPLIER_PAYMENT_STATUS_LABELS[payment.status as SupplierPaymentStatus]}
                      </Badge>
                      <Badge colorPalette="purple" size="sm">
                        {SUPPLIER_PAYMENT_MODE_LABELS[payment.payment_mode as SupplierPaymentMode]}
                      </Badge>
                    </HStack>
                    <Heading
                      size="md"
                      lineHeight="1.3"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                    >
                      Règlement {formatCurrency(payment.amount)}
                    </Heading>
                    {payment.suppliers && (
                      <Text fontSize="sm" color="gray.500">
                        {payment.suppliers.name}
                      </Text>
                    )}
                  </>
                )}
                {!payment && <Heading size="md">Détail du règlement</Heading>}
              </Box>

              {/* Close */}
              <IconButton variant="ghost" size="sm" onClick={onClose} aria-label="Fermer">
                <X size={18} />
              </IconButton>
            </Box>
          </Drawer.Header>

          <Drawer.Body p={0} display="flex" flexDirection="column" height="100%">
            {isLoading && <LoadingState message="Chargement..." />}
            {error && <ErrorState message="Règlement non trouvé" />}

            {payment && (
              <VStack align="stretch" gap={0} flex={1} height="100%" overflow="auto">
                {/* Informations principales */}
                <HStack
                  gap={4}
                  px={6}
                  pt={4}
                  pb={3}
                  bg="gray.50"
                  borderBottomWidth="1px"
                  borderColor="gray.200"
                  flexWrap="wrap"
                >
                  <HStack gap={2} flex={1} minW="140px">
                    <Box color="blue.500">
                      <Calendar size={16} />
                    </Box>
                    <VStack align="flex-start" gap={0}>
                      <Text fontSize="xs" color="gray.600" fontWeight="medium">
                        Date de paiement
                      </Text>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                        {formatDate(payment.payment_date)}
                      </Text>
                    </VStack>
                  </HStack>

                  <HStack gap={2} flex={1} minW="140px">
                    <Box color="purple.500">
                      <CreditCard size={16} />
                    </Box>
                    <VStack align="flex-start" gap={0}>
                      <Text fontSize="xs" color="gray.600" fontWeight="medium">
                        Mode
                      </Text>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                        {SUPPLIER_PAYMENT_MODE_LABELS[payment.payment_mode as SupplierPaymentMode]}
                      </Text>
                    </VStack>
                  </HStack>

                  <HStack gap={2} flex={1} minW="140px">
                    <Box color="green.500">
                      <CreditCard size={16} />
                    </Box>
                    <VStack align="flex-start" gap={0}>
                      <Text fontSize="xs" color="gray.600" fontWeight="medium">
                        Montant
                      </Text>
                      <Text fontSize="lg" fontWeight="bold" color="green.600">
                        {formatCurrency(payment.amount)}
                      </Text>
                    </VStack>
                  </HStack>
                </HStack>

                {/* Fournisseur */}
                {payment.suppliers && (
                  <Box px={6} pt={4}>
                    <Card.Root variant="outline">
                      <Card.Header
                        py={3}
                        bg="gray.50"
                        borderBottomWidth="1px"
                        borderColor="gray.200"
                      >
                        <HStack>
                          <Truck size={16} />
                          <Heading size="sm">Fournisseur</Heading>
                        </HStack>
                      </Card.Header>
                      <Card.Body>
                        <HStack justify="space-between">
                          <VStack align="flex-start" gap={0}>
                            <Text fontSize="sm" fontWeight="medium">
                              {payment.suppliers.name}
                            </Text>
                          </VStack>
                          <Badge variant="outline" size="sm" fontFamily="mono">
                            {payment.suppliers.code}
                          </Badge>
                        </HStack>
                      </Card.Body>
                    </Card.Root>
                  </Box>
                )}

                {/* Compte bancaire - TODO: ajouter quand les infos seront dans la query */}
                <Box px={6} pt={4}>
                  <Card.Root variant="outline">
                    <Card.Header py={3} bg="gray.50" borderBottomWidth="1px" borderColor="gray.200">
                      <HStack>
                        <Landmark size={16} />
                        <Heading size="sm">Compte bancaire</Heading>
                      </HStack>
                    </Card.Header>
                    <Card.Body>
                      <Text fontSize="sm" color="gray.500">
                        Compte principal de la résidence
                      </Text>
                    </Card.Body>
                  </Card.Root>
                </Box>

                {/* Factures réglées */}
                <Box px={6} pt={4}>
                  <Card.Root variant="outline">
                    <Card.Header py={3} bg="gray.50" borderBottomWidth="1px" borderColor="gray.200">
                      <HStack justify="space-between">
                        <HStack>
                          <FileText size={16} />
                          <Heading size="sm">Factures réglées</Heading>
                        </HStack>
                        <Badge size="sm">{payment.supplier_payment_allocations?.length || 0} facture(s)</Badge>
                      </HStack>
                    </Card.Header>
                    <Card.Body p={0}>
                      {payment.supplier_payment_allocations && payment.supplier_payment_allocations.length > 0 ? (
                        <Table.Root size="sm">
                          <Table.Header>
                            <Table.Row>
                              <Table.ColumnHeader>N° Facture</Table.ColumnHeader>
                              <Table.ColumnHeader>Réf. interne</Table.ColumnHeader>
                              <Table.ColumnHeader textAlign="right">Total TTC</Table.ColumnHeader>
                              <Table.ColumnHeader textAlign="right">Alloué</Table.ColumnHeader>
                            </Table.Row>
                          </Table.Header>
                          <Table.Body>
                            {payment.supplier_payment_allocations.map((allocation) => (
                              <Table.Row key={allocation.id}>
                                <Table.Cell>
                                  <Text fontFamily="mono" fontSize="sm">
                                    {allocation.supplier_invoices?.invoice_number || '-'}
                                  </Text>
                                </Table.Cell>
                                <Table.Cell>
                                  <Text fontSize="xs" color="gray.500">
                                    {allocation.supplier_invoices?.internal_reference || '-'}
                                  </Text>
                                </Table.Cell>
                                <Table.Cell textAlign="right">
                                  <Text fontSize="sm" fontFamily="mono">
                                    {formatCurrency(allocation.supplier_invoices?.total_ttc)}
                                  </Text>
                                </Table.Cell>
                                <Table.Cell textAlign="right">
                                  <Text
                                    fontSize="sm"
                                    fontFamily="mono"
                                    fontWeight="medium"
                                    color="green.600"
                                  >
                                    {formatCurrency(allocation.amount_allocated)}
                                  </Text>
                                </Table.Cell>
                              </Table.Row>
                            ))}
                            {/* Total */}
                            <Table.Row bg="gray.100">
                              <Table.Cell colSpan={3} textAlign="right">
                                <Text fontWeight="bold">Total alloué</Text>
                              </Table.Cell>
                              <Table.Cell textAlign="right">
                                <Text fontFamily="mono" fontWeight="bold" color="green.600">
                                  {formatCurrency(totalAllocated)}
                                </Text>
                              </Table.Cell>
                            </Table.Row>
                          </Table.Body>
                        </Table.Root>
                      ) : (
                        <Box p={4}>
                          <Text fontSize="sm" color="gray.500" textAlign="center">
                            Aucune allocation
                          </Text>
                        </Box>
                      )}
                    </Card.Body>
                  </Card.Root>
                </Box>

                {/* Historique */}
                <Box px={6} pt={4} pb={6}>
                  <Card.Root variant="outline">
                    <Card.Header py={3}>
                      <Heading size="sm">Historique</Heading>
                    </Card.Header>
                    <Card.Body>
                      <VStack align="stretch" gap={3}>
                        <HStack gap={3}>
                          <Box color="gray.400">
                            <Clock size={16} />
                          </Box>
                          <VStack align="flex-start" gap={0}>
                            <Text fontSize="sm" fontWeight="medium">
                              Créé le
                            </Text>
                            <Text fontSize="xs" color="gray.600">
                              {formatDateTime(payment.created_at)}
                            </Text>
                          </VStack>
                        </HStack>

                        {payment.updated_at && payment.updated_at !== payment.created_at && (
                          <HStack gap={3}>
                            <Box color="blue.500">
                              <Clock size={16} />
                            </Box>
                            <VStack align="flex-start" gap={0}>
                              <Text fontSize="sm" fontWeight="medium">
                                Modifié le
                              </Text>
                              <Text fontSize="xs" color="gray.600">
                                {formatDateTime(payment.updated_at)}
                              </Text>
                            </VStack>
                          </HStack>
                        )}
                      </VStack>
                    </Card.Body>
                  </Card.Root>
                </Box>
              </VStack>
            )}
          </Drawer.Body>
        </Drawer.Content>
      </Drawer.Positioner>
    </Drawer.Root>
  )
}
